import { computedFrom, BindingEngine } from "aurelia-binding";
import { StepModel } from "../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { transient, autoinject } from "aurelia-framework";
import { ActiveMenuItemsService } from "../../../../../services/basics/active-menu-items.service";
import { Reflection } from "../../../../../utilities/reflection";
import { Router } from "aurelia-router";
import { I18N } from "aurelia-i18n";
import { BaseEntity } from "../../base-entity";
import { EntityItem } from "../../entity-item";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { ValidationsClientService } from "../../../../../services/validations/validations-client.service";
import { BaseValidationResponse, ValidationResult } from "../../../../../models/validations/validations.models";
import { Guid } from "../../../../../utilities/guid";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { MenuItem } from "../../../../components/menu/menu-item";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { CustomReportModel, DeleteCustomReportRequest } from "../../../../../models/custom-reports/custom-reports.models";
import { CustomReportService } from "../../../../../services/custom-report/custom-report.service";
import { CustomReportItems } from "./steps/custom-report-items";
import { CustomReportHistoryTrail } from "./steps/custom-report-history-trail";
import { SourceType } from "../../../../../models/queries/queries.models";
import { CustomReportExports } from "./steps/custom-report-exports";
import { SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";

@autoinject()
@transient()
export class CustomReport extends BaseEntity<CustomReportModel> {

    public entityItem: EntityItem;
    public isExporting: boolean = false;

    @computedFrom(Reflection.getPropertyPath<CustomReport>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const prevInstruction = this.router.currentInstruction.previousInstruction;
        return prevInstruction
            ? prevInstruction.config.title
            : this.i18N.tr('back-text');
    }

    constructor(private customReportService: CustomReportService, private activeMenuItemsService: ActiveMenuItemsService, router: Router, i18N: I18N,
        bindingEngine: BindingEngine, notificationService: NotificationClientService, eventAggregator: EventAggregator,
        validationsClientService: ValidationsClientService,
        private dialogClient: DialogClientService,
        private customReportItems: CustomReportItems,
        private customReportExports: CustomReportExports,
        private signalClientService: SignalClientService,
        private customReportHistoryTrail: CustomReportHistoryTrail,
        private userClaimsService: UserClaimsService) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService);
    }

    activate(params: any, navigationInstruction: any) {
        this.activeMenuItemsService.setActiveFromMenuItem('CustomReports');
        return super.activate(params, navigationInstruction);
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    protected getEntityType(): EntityTypes {
        return this.item ? this.item.entityType : null;
    }

    protected getItem(id: number): Promise<CustomReportModel> {
        return new Promise<CustomReportModel>((resolve, reject) => {
            this.customReportService.getCustomReport(id).then(response => {
                resolve(response.customReport);
            }, reject);
        });

    }

    attached() {
        super.attached();
        if (this.router.currentInstruction) {
            this.router.currentInstruction.config.title = this.item.name;
        }

        this.signalClientService.subscribe(SignalFunctions.exportCustomReportReady, this.onExportCustomReportReady);
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.exportCustomReportReady, this.onExportCustomReportReady);
        super.detached();
    }

    onExportCustomReportReady = (params: {
        customReportId: number,
        entityAttachmentId: number
    }) => {
        if (this.item.customReportId == params.customReportId) {
            this.isExporting = false;
        }
    }

    protected getItemId(): number {
        return this.item ? this.item.customReportId : null;
    }

    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.CustomReports;
    }

    protected getSteps(): StepModel[] {
        return [
            {
                lazyLoad: true,
                title: "Items",
                step: 1,
                vm: this.customReportItems,
                show: true
            },
            {
                lazyLoad: true,
                title: "Exports",
                step: 2,
                vm: this.customReportExports,
                show: true
            },
            {
                lazyLoad: true,
                title: "History Trail",
                step: 3,
                vm: this.customReportHistoryTrail,
                show: true
            }];
    }

    protected getUpdateRoute(): string {
        return RightShipQiRouterConstants.RouteNames.CustomReports;
    }

    protected initializeItemForCreate(): Promise<CustomReportModel> | CustomReportModel {
        const customReport: CustomReportModel = {
            entityType: EntityTypes.CustomReport,
            identifier: Guid.generateGuid(),
            customReportId: 0,
            customReportTypeId: 0,
            internationalMaritimeOrganizationNumbers: null,
            name: null,
            selectClause: null,
            whereClause: null
        };

        return customReport;
    }

    protected saveItem(): Promise<BaseValidationResponse> {
        throw new Error("Method not implemented.");
    }

    getMenuItems(): MenuItem[] {
        return [
            {
                isVisible: true,
                click: () => {
                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.ExportCustomReportDialog, {
                        onConfirm: () => {
                            this.isExporting = true;
                            this.customReportService.exportCustomReport(this.item.customReportId).then(() => { 
                                this.isExporting = false;
                            }).catch(() => {
                                this.isExporting = false;
                            });
                        }
                    }, false, false, false, "right");
                },
                item: { text: "Export", import: false, export: true, history: false, edit: false, remove: false },
                classList: ''
            },
            {
                isVisible: () =>
                    this.userClaimsService.hasPermission(Claims.CanUpdateCustomReports),
                click: () => {
                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.EditCustomReportDialog, {
                        onComplete: () => {
                            this.getEntity(this.item.customReportId);
                        },
                        id: this.item ? this.item.customReportId : null
                    }, true, false, false, 'right');
                },
                item: { text: "Edit", import: false, export: false, history: false, edit: true, remove: false },
                classList: ''
            },
            {
                isVisible: () =>
                    this.userClaimsService.hasPermission(Claims.CanDeleteCustomReports),
                click: () => {
                    this.dialogClient.show(RightShipDialogServiceConstants.URLs.CustomReportDeleteDialog, { items: [this.item], deleteFunc: this.deleteCustomReport }, true, false, false, 'right');
                },
                item: { text: "Remove", import: false, export: false, history: false, edit: false, remove: true },
                classList: ''
            }
        ].filter(x => x.isVisible);
    }

    deleteCustomReport = (items: any[], reason: string) => {

        return new Promise<void>((resolve, reject) => {

            const request: DeleteCustomReportRequest = {
                ids: items.map(c => c.customReportId),
                reason: reason
            }

            this.customReportService.deleteCustomReports(request).then(response => {
                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                    reject();
                }
                else {
                    this.notificationService.show(this.i18N.tr('successfully-deleted-text'), "", NotificationType.Success, 5000);
                    //go back to custom reports page after delete custom report
                    this.goBack();
                    resolve();
                }
            }).catch(x => {
                const response: {
                    exception: string;
                    details: string;
                    validationResult: ValidationResult;
                } = typeof (x.response) === 'object'
                        ? x.response
                        : (x.response ? JSON.parse(x.response) : {});
                if (!response.exception || !response.details) {
                    this.notificationService.show(this.i18N.tr('failed-custom-report-deletion-text'), "", NotificationType.Error, 5000);
                } else if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                }
                reject();
            });
        })
    };

    openShowAllDataDialog() {
        this.dialogClient.show(RightShipDialogServiceConstants.URLs.ShowAllDataDialog, {
            itemSource: {
                columns: this.customReportItems.columns,
                initialFilters: this.customReportItems.filters,
                sourceType: SourceType.ODataUrl,
                source: "odata/" + this.customReportItems.customReportType + "?customReportId=" + this.item.customReportId
            }
        }, true, false, true);
    }
}